/**
 * InviteFriendsButton — Phase 71
 *
 * A compact icon button that lives in the AppShell header next to the
 * notification bell. Tapping it opens a calm, on-brand modal where the
 * user can:
 *   • enter up to 5 friend email addresses
 *   • write an optional personal message
 *   • copy a personal share link (their referral_code)
 *
 * On submit it calls POST /api/invitations/send and surfaces a single
 * toast summarising what landed (sent / skipped / invalid).
 *
 * No analytics events, no nags. The success state celebrates briefly and
 * resets so the user can invite more if they want.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Gift, Send, Copy, Check, Loader2, Mail,
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SLOTS = 5;

async function freshToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch { return null; }
}

export default function InviteFriendsButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl"
              onClick={() => setOpen(true)}
              aria-label="Invite friends"
              data-testid="invite-friends-trigger"
            >
              <Gift className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Invite friends</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <InviteFriendsModal open={open} onOpenChange={setOpen} />
    </>
  );
}

// ---------------------------------------------------------------- Modal ----
function InviteFriendsModal({ open, onOpenChange }) {
  const [emails, setEmails] = useState(Array(SLOTS).fill(''));
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [referral, setReferral] = useState(null); // { code, url } | null
  const [copied, setCopied] = useState(false);

  // Reset state whenever the modal re-opens.
  useEffect(() => {
    if (open) {
      setEmails(Array(SLOTS).fill(''));
      setMessage('');
      setCopied(false);
    }
  }, [open]);

  // Fetch (and lazily create) the user's referral link the first time
  // the modal opens.
  useEffect(() => {
    if (!open || referral) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await freshToken();
        const { data } = await axios.get(
          `${BACKEND_URL}/api/invitations/referral-link`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled && data?.ok) {
          setReferral({ code: data.code, url: data.url });
        }
      } catch {
        // Non-blocking — the modal still works without the share link.
      }
    })();
    return () => { cancelled = true; };
  }, [open, referral]);

  const filledCount = useMemo(
    () => emails.map((e) => e.trim()).filter(Boolean).length,
    [emails],
  );

  const updateSlot = useCallback((idx, value) => {
    setEmails((prev) => prev.map((v, i) => (i === idx ? value : v)));
  }, []);

  const handleCopy = useCallback(async () => {
    if (!referral?.url) return;
    try {
      await navigator.clipboard.writeText(referral.url);
      setCopied(true);
      toast.success('Referral link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy. Long-press to copy manually.');
    }
  }, [referral]);

  const handleSend = useCallback(async () => {
    const payload = emails.map((e) => e.trim()).filter(Boolean);
    if (payload.length === 0) {
      toast.error('Add at least one email address.');
      return;
    }
    setSending(true);
    try {
      const token = await freshToken();
      const { data } = await axios.post(
        `${BACKEND_URL}/api/invitations/send`,
        { emails: payload, personal_message: message.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const sent = data?.sent || 0;
      const skipped = (data?.skipped_existing || []).length;
      const invalid = (data?.invalid || []).length;
      const failures = (data?.failures || []).length;
      if (sent > 0) {
        toast.success(
          `Sent ${sent} invitation${sent === 1 ? '' : 's'}.`,
          {
            description: [
              skipped ? `${skipped} already invited` : null,
              invalid ? `${invalid} skipped (invalid)` : null,
              failures ? `${failures} failed` : null,
            ].filter(Boolean).join(' · ') || undefined,
          },
        );
        // Soft-close on success.
        onOpenChange(false);
      } else if (skipped + invalid + failures > 0) {
        toast.warning('Nothing new was sent.', {
          description: 'Either already invited or invalid — check the addresses and try again.',
        });
      } else {
        toast.error('No invitations were sent.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || 'Could not send invitations. Please try again.');
    } finally {
      setSending(false);
    }
  }, [emails, message, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg rounded-2xl"
        data-testid="invite-friends-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Mail className="h-5 w-5 text-[hsl(var(--gold))]" />
            Invite friends to 2120
          </DialogTitle>
          <DialogDescription>
            Calm financial clarity is better shared. Invite up to five people — we’ll send a brief, friendly note from you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          {/* Email rows */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Friends’ emails
            </Label>
            <div className="space-y-2">
              {emails.map((val, idx) => (
                <Input
                  key={idx}
                  type="email"
                  placeholder={`friend${idx + 1}@example.com`}
                  value={val}
                  onChange={(e) => updateSlot(idx, e.target.value)}
                  className="rounded-lg"
                  autoComplete="off"
                  spellCheck={false}
                  data-testid={`invite-email-${idx}`}
                />
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {filledCount} of {SLOTS} filled · max 25 invites/day
            </div>
          </div>

          {/* Optional personal message */}
          <div className="space-y-1.5">
            <Label
              htmlFor="invite-message"
              className="text-xs uppercase tracking-wider text-muted-foreground"
            >
              Personal note <span className="normal-case text-muted-foreground/70">(optional)</span>
            </Label>
            <Textarea
              id="invite-message"
              placeholder="Why you think they’d like 2120…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={600}
              rows={3}
              className="rounded-lg resize-none"
              data-testid="invite-message"
            />
            <div className="text-[10px] text-muted-foreground text-right tabular-nums">
              {message.length}/600
            </div>
          </div>

          {/* Personal share link */}
          {referral?.url && (
            <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-medium">Or share your personal link</div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCopy}
                  className="h-7 px-2 rounded-lg"
                  data-testid="invite-copy-link"
                >
                  {copied
                    ? <><Check className="h-3.5 w-3.5 mr-1" /> Copied</>
                    : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
                </Button>
              </div>
              <div
                className="text-[11px] font-mono break-all text-muted-foreground"
                data-testid="invite-referral-link"
              >
                {referral.url}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            className="rounded-lg"
            data-testid="invite-cancel"
          >
            Close
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || filledCount === 0}
            className="rounded-lg"
            data-testid="invite-send"
          >
            {sending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Send className="h-4 w-4 mr-2" />}
            {sending ? 'Sending…' : `Send ${filledCount || ''} invitation${filledCount === 1 ? '' : 's'}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
